import React, { useCallback, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  FeedbackImpactDetailsProps,
  FeedbackResolved,
} from '../FeedbackImpactDetails';
import {
  IndicatorByService,
  IndicatorSectionKey,
  IndicatorWithUpdate,
  indicatorSectionsInOrder,
} from '../../../ReportBuilder/Components/ImpactIndicator/utils';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { useClient } from 'shared/apollo';
import {
  CommentsByStageIndicatorWithUpdate,
  getIndicatorItemTitle,
  getIndicatorWithUpdateDescription,
  getIndicatorWithUpdateExtraDetails,
  getIndicatorWithUpdateId,
  getIndicatorWithUpdateTitle,
  indicatorWithUpdateKeys,
  parseCommentDetailsWithIndicatorWithUpdate,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactSummary';
import {
  FeedbackCommentBodyWithIndex,
  IMPACT_SUMMARY_QUESTION_ID,
} from '../../utils';
import { FeedbackCommentProps } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { toUserType } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import ImpactDetailStageIcons, {
  GenericFeedbackProps,
  ImpactDetailStageIconsProps,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactDetails/ImpactDetailStageIcons';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { updateAnswer } from '../../MakeEdits/api';
import FeedbackVerificationButtons, {
  FeedbackVerificationButtonsProps,
} from '../FeedbackVerificationButtons';
import ImpactSummaryEdit from './ImpactSummaryEdit';
import JSON5 from 'json5';

type RenderBottomButtonsProps = Omit<
  FeedbackVerificationButtonsProps,
  'handleEditClick' | 'handleVerifiedClick'
> & {
  isSectionCompleted: boolean;
  resolved: FeedbackResolved<IndicatorWithUpdate>;
  handleEditClick: (
    sectionKey: IndicatorSectionKey,
    item: IndicatorWithUpdate,
    itemKey: keyof IndicatorWithUpdate,
  ) => unknown;
  handleVerifiedClick: (
    sectionKey: IndicatorSectionKey,
    item: IndicatorWithUpdate,
    itemKey: keyof IndicatorWithUpdate,
  ) => unknown;
};

const FeedbackRenderBottomButtons: React.FC<RenderBottomButtonsProps &
  GenericFeedbackProps & {
    itemKey: keyof IndicatorWithUpdate;
    item: IndicatorWithUpdate;
    index: number;
    totalItems: number;
  }> = ({
  item,
  itemKey,
  requestState,
  className,
  index,
  totalItems,
  sectionKey,
  allowEdits,
  resolved,
  isSectionCompleted,
  feedbackStatus,
  handleEditClick,
  handleVerifiedClick,
}) => {
  const isCompleted =
    isSectionCompleted || resolved[sectionKey]?.[item.id]?.[itemKey];
  return (
    <FeedbackVerificationButtons
      allowEdits={allowEdits}
      feedbackStatus={feedbackStatus}
      isCompleted={isCompleted}
      handleEditClick={() => handleEditClick(sectionKey, item, itemKey)}
      handleVerifiedClick={() => handleVerifiedClick(sectionKey, item, itemKey)}
      requestState={requestState}
      className={index === totalItems - 1 ? className : `${className} margin`}
    />
  );
};

export const parseImpactSummaryAnswer = (
  text: string | undefined,
): IndicatorByService | undefined => {
  if (!text) return undefined;
  try {
    return JSON5.parse(text);
  } catch (e) {
    return undefined;
  }
};

type Options = {
  [x in IndicatorSectionKey]?:
    | undefined
    | {
        [indicatorId: number]:
          | undefined
          | {
              keys: (keyof IndicatorWithUpdate)[];
              indicator: IndicatorWithUpdate;
            };
        options: IndicatorWithUpdate[];
      };
};

const getOptions = (
  answeredIndicators: IndicatorByService | undefined,
  commentsByStage: CommentsByStageIndicatorWithUpdate,
): { options: Options; total: number } => {
  let total = 0;

  const options: Options = {};

  if (!answeredIndicators) return { options, total };

  for (const sectionKey of indicatorSectionsInOrder) {
    const indicators = answeredIndicators[sectionKey];
    for (const o of indicators) {
      for (const k of indicatorWithUpdateKeys()) {
        const comments: FeedbackCommentProps[] =
          commentsByStage[sectionKey]?.[o.id]?.[k] || [];
        if (comments.length > 0) {
          if (!options[sectionKey]) {
            options[sectionKey] = {
              options: [o],
            };
          } else {
            if (
              options[sectionKey]!.options.findIndex(x => x.id === o.id) === -1
            ) {
              options[sectionKey]!.options.push(o);
            }
          }
          if (!options[sectionKey]![o.id]) {
            options[sectionKey]![o.id] = {
              keys: [],
              indicator: o,
            };
          }
          options[sectionKey]![o.id]!.keys.push(k);
          total += 1;
        }
      }
    }
  }

  return { options, total };
};

const parseFeedbackCommentsWithDetails = (
  comments: FeedbackCommentBodyWithIndex[],
) => {
  const result: CommentsByStageIndicatorWithUpdate = {};
  for (const c of comments) {
    const details = parseCommentDetailsWithIndicatorWithUpdate(c.details);
    if (!details) continue;
    const comment: FeedbackCommentProps = {
      userId: c.userId,
      commentId: c.commentId,
      createdAt: c.createdAt,
      name: c.userName,
      text: c.comment,
      accountType: toUserType(c.userType),
      details: c.details,
      email: c.userEmail,
      updatedAt: c.createdAt,
    };
    if (!result[details.sectionKey]) {
      result[details.sectionKey] = {};
    }
    if (!result[details.sectionKey][details.indicatorId]) {
      result[details.sectionKey][details.indicatorId] = {};
    }
    if (!result[details.sectionKey][details.indicatorId][details.subQuestion]) {
      result[details.sectionKey][details.indicatorId][details.subQuestion] = [];
    }
    result[details.sectionKey][details.indicatorId][details.subQuestion].push(
      comment,
    );
  }
  return result;
};

type ImpactSummaryDetailsProps = FeedbackImpactDetailsProps

const StyledDiv = styled.div``;

const ImpactSummaryDetails: React.FC<ImpactSummaryDetailsProps> = ({
  answer,
  feedback,
  reportId,
  allowEdits,
  feedbackStatus,
  requestState,
  isSectionCompleted,
  currentUserName,
  currentUserId,
  currentUserType,
  currentUserEmail,
  handleVerifiedClick,
}) => {
  const client = useClient();
  const [saveRequestState, makeSaveRequest] = useGraphQLRequest<unknown>();
  const [totalResolved, setTotalResolved] = useState<number>(0);
  const [resolved, setResolved] = useState<
    FeedbackResolved<IndicatorWithUpdate>
  >({});
  const [edit, setEdit] = useState<{
    sectionKey: IndicatorSectionKey;
    item: IndicatorWithUpdate;
    itemKey: keyof IndicatorWithUpdate;
  }>();
  const [answeredIndicators, setAnsweredIndicators] = useState<
    IndicatorByService | undefined
  >(parseImpactSummaryAnswer(answer));
  const [commentsByStage, setCommentsByStage] = useState<
    CommentsByStageIndicatorWithUpdate
  >(parseFeedbackCommentsWithDetails(feedback));

  useEffect(() => {
    setAnsweredIndicators(parseImpactSummaryAnswer(answer));
  }, [answer, setAnsweredIndicators]);

  const { options, total } = useMemo(
    () => getOptions(answeredIndicators, commentsByStage),
    [answeredIndicators, commentsByStage],
  );

  useEffect(() => {
    if (!answeredIndicators) return;
    makeSaveRequest(
      updateAnswer(client!, {
        reportId,
        questionId: IMPACT_SUMMARY_QUESTION_ID,
        answerId: undefined,
        answer: JSON.stringify(answeredIndicators),
      }),
    );
  }, [answeredIndicators]);

  useEffect(() => {
    if (total && total === totalResolved) {
      handleVerifiedClick();
    }
  }, [total, totalResolved, handleVerifiedClick]);

  const handleCloseModal = useCallback(() => {
    setEdit(undefined);
  }, [setEdit]);

  const handleUpdate = useCallback(
    (
      sectionKey: IndicatorSectionKey,
      item: IndicatorWithUpdate,
      itemKey: keyof IndicatorWithUpdate,
      answer: IndicatorWithUpdate[keyof IndicatorWithUpdate],
    ) => {
      setAnsweredIndicators(s => {
        if (!s) return s;
        const newState: IndicatorByService = { ...s };
        const i = newState?.[sectionKey]?.find(x => x.id === item.id);
        if (!i) return s;
        (i[itemKey] as typeof answer) = answer;
        return newState;
      });
    },
    [setAnsweredIndicators],
  );

  const handleEdit = useCallback(
    (
      sectionKey: IndicatorSectionKey,
      item: IndicatorWithUpdate,
      itemKey: keyof IndicatorWithUpdate,
    ) => {
      setEdit({
        sectionKey,
        item,
        itemKey,
      });
    },
    [setEdit],
  );

  const handleVerified = useCallback(
    (
      sectionKey: IndicatorSectionKey,
      item: IndicatorWithUpdate,
      itemKey: keyof IndicatorWithUpdate,
    ) => {
      setResolved(s => {
        const newState = { ...s };
        if (!newState[sectionKey]) {
          newState[sectionKey] = {};
        }
        if (!newState[sectionKey]![item.id]) {
          newState[sectionKey]![item.id] = {};
        }
        newState[sectionKey]![item.id]![itemKey] = true;
        return newState;
      });
      setTotalResolved(n => n + 1);
    },
    [setResolved, setTotalResolved],
  );

  const getComments: ImpactDetailStageIconsProps<
    IndicatorWithUpdate
  >['getComments'] = useCallback(
    ({ sectionKey, indicatorId, subQuestion }): FeedbackCommentProps[] =>
      commentsByStage[sectionKey]?.[indicatorId]?.[subQuestion] || [],
    [commentsByStage],
  );

  if (!answer) {
    return <TrueImpactLoading />;
  }

  if (!answeredIndicators) {
    return (
      <TrueImpactText variant="body-bold" color="critical">
        Impact model builder was not saved properly. Please contact support
        about this report.
      </TrueImpactText>
    );
  }

  return (
    <StyledDiv>
      {indicatorSectionsInOrder.map(key => {
        const o = options[key] || {
          options: [],
        };
        const getKeys = (
          k: IndicatorSectionKey,
          indicator: IndicatorWithUpdate,
        ): (keyof IndicatorWithUpdate)[] => o[indicator.id]?.keys || [];
        return (
          <ImpactDetailStageIcons<IndicatorWithUpdate, RenderBottomButtonsProps>
            key={key}
            itemTitle={getIndicatorItemTitle}
            reportId={reportId}
            questionId={IMPACT_SUMMARY_QUESTION_ID}
            currentUserName={currentUserName}
            currentUserId={currentUserId}
            currentUserType={currentUserType}
            currentUserEmail={currentUserEmail}
            sectionId="impact_summary"
            sectionKey={key}
            options={o.options}
            getIndicatorId={getIndicatorWithUpdateId}
            getTitle={getIndicatorWithUpdateTitle}
            getComments={getComments}
            getDescription={getIndicatorWithUpdateDescription}
            getExtraDetails={getIndicatorWithUpdateExtraDetails}
            setCommentsByStage={setCommentsByStage}
            getKeys={getKeys}
            IndicatorChildComponent={{
              props: {
                allowEdits,
                feedbackStatus,
                requestState,
                className: 'feedback-verification-buttons',
                handleEditClick: handleEdit,
                handleVerifiedClick: handleVerified,
                resolved,
                isSectionCompleted,
              },
              component: FeedbackRenderBottomButtons,
            }}
          />
        );
      })}
      {edit && (
        <ImpactSummaryEdit
          reportId={reportId}
          open={true}
          item={edit.item}
          itemKey={edit.itemKey}
          indicators={answeredIndicators}
          sectionKey={edit.sectionKey}
          handleUpdate={handleUpdate}
          handleVerified={handleVerified}
          handleCloseModal={handleCloseModal}
        />
      )}
    </StyledDiv>
  );
};

export default ImpactSummaryDetails;
